import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner, Badge,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCustomerUpcoming } from '../../../api/hooks';
import type { CustomerUpcomingItem } from '../../../api/types';

// FEAT-026 Upcoming Deliverables. Six-week look-ahead. "Pending UAT" badge highlights
// items the customer themselves owe action on (linking to a Teams UAT folder is a
// future tech-debt item once OpsMgr FEAT-025 ships).
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  dateRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
});

export function CustomerUpcomingPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useCustomerUpcoming(projectId, 6);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>What's Coming Next</Title2>
        <Body1 className={styles.hint}>
          Upcoming deliverables on your projects for the next six weeks. Anything labelled "Pending UAT" needs you.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading deliverables…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.items.length === 0 ? (
        <Body1>Nothing scheduled in the next six weeks.</Body1>
      ) : (
        <div className={styles.list}>
          {data.items.map((i: CustomerUpcomingItem, idx: number) => (
            <div key={idx} className={styles.item}>
              <div className={styles.header}>
                <Title3>{i.customerTitle}</Title3>
                {i.pendingUat && <Badge appearance="filled" color="warning">Pending UAT</Badge>}
              </div>
              {i.customerDescription ? <Body1>{i.customerDescription}</Body1> : null}
              <div className={styles.dateRow}>
                <Caption1>Targeted by {new Date(i.targetDate).toLocaleDateString()}</Caption1>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
